import { currentUser, GoogleCallback, HomeboundUser } from "@homebound/auth-components";
import { BeamProvider, Toast } from "@homebound/beam";
import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProps, AuthWrapper } from "src/routes/auth/AuthWrapper";
import { SignIn } from "src/routes/auth/SignIn";
import { InsideMapsPage } from "src/routes/insidemaps/InsideMapsPage";
import { insideMapsPath, signInPath } from "src/routes/routesDef";

export function App() {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<HomeboundUser>();

  useEffect(() => {
    onLoad().catch(console.error);
  }, []);

  async function onLoad() {
    const { data: user } = await currentUser();
    setUser(user);
    setIsAuthenticated(user !== undefined);
    setIsAuthenticating(false);
  }

  const authProps: AuthProps = { isAuthenticated, setIsAuthenticated, user, setUser, isAuthenticating };

  useDeepLink(isAuthenticated);

  if (isAuthenticating) {
    return <div />;
  }

  return (
    <BeamProvider>
      <Toast />
      <Routes>
        <Route path="/" element={<Navigate to={insideMapsPath} replace />} />
        <Route
          path={insideMapsPath}
          element={
            <AuthWrapper requireAuth authProps={authProps}>
              <InsideMapsPage />
            </AuthWrapper>
          }
        />
        <Route
          path={signInPath}
          element={
            <AuthWrapper requireAuth={false} authProps={authProps}>
              <SignIn />
            </AuthWrapper>
          }
        />
        <Route
          path="/auth/gcallback"
          element={
            <AuthWrapper requireAuth={false} authProps={authProps}>
              <GoogleCallback />
            </AuthWrapper>
          }
        />
      </Routes>
    </BeamProvider>
  );
}

/**
 * Save a deep link to redirect to after sign in
 *
 * If the user isn't authenticated, we check for a path to set as the deepLink.
 * We skip saving the deepLink if the user navigates to "/signIn".
 * Additionally, since we redirect to the main page when a user logs in or navigates to "/",
 * these paths don't need to be saved as a deepLink.
 */
function useDeepLink(isAuthenticated: boolean) {
  const location = useLocation();

  useEffect(() => {
    const deepLink = localStorage.getItem("deepLink");
    const skipDeepLink = [signInPath, "/", insideMapsPath].includes(location.pathname);
    if (!isAuthenticated && !skipDeepLink && !deepLink) {
      const currentPath = location.pathname + location.search;
      localStorage.setItem("deepLink", currentPath);
    }
  }, [isAuthenticated, location]);
}
